import React, { useState, useEffect } from "react";
import { Card, Alert, Typography, Space, Button, Divider, Spin } from "antd";
import { LockOutlined, CrownOutlined, CheckCircleOutlined, CustomerServiceOutlined, PlusOutlined } from "@ant-design/icons";
import { useAuth0 } from "@auth0/auth0-react";
import { useSubscription } from "../contexts/subscription-context";
import { CreateOrganizationModal } from "./CreateOrganizationModal";
import { useOrganization } from "../contexts/organization-context";
import apiClient from "../utils/axios";

const { Title, Text, Paragraph } = Typography;

interface AccessStatus {
  has_access: boolean;
  reason?: string;
  organizations: Array<{
    id: string;
    name: string;
    subscription_status?: string;
    free_trial_expires_at?: string;
    is_owner: boolean;
  }>;
  needs_organization: boolean;
  user_trial_expires_at?: string;
  trial_days_remaining?: number;
}

interface SubscriptionGateProps {
  children: React.ReactNode;
  feature?: string;
}

export const SubscriptionGate: React.FC<SubscriptionGateProps> = ({ 
  children, 
  feature = "this feature" 
}) => {
  const { hasActiveSubscription, loading: subscriptionLoading, subscriptionData, refreshSubscriptionData, forceFullRefresh } = useSubscription();
  const { getAccessTokenSilently } = useAuth0();
  const { refreshOrganizations } = useOrganization();
  const [accessStatus, setAccessStatus] = useState<AccessStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const checkAccess = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const response = await apiClient.get("/api/access/status", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAccessStatus(response.data);
      console.log("[SubscriptionGate] Access status:", response.data);
    } catch (error) {
      console.error("Error checking access:", error);
      setAccessStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAccess();
  }, []);

  // Refresh access status when subscription data changes
  useEffect(() => {
    if (subscriptionData?.is_free_trial || hasActiveSubscription) {
      console.log("[SubscriptionGate] Subscription context updated, refreshing access status");
      checkAccess();
    }
  }, [subscriptionData?.is_free_trial, hasActiveSubscription]);

  const handleCreateSuccess = async () => {
    setShowCreateModal(false);
    await refreshOrganizations();
    await forceFullRefresh?.(); // Force complete refresh of subscription data
    await checkAccess(); // Refresh access status
  };

  if (loading || subscriptionLoading) {
    return (
      <div style={{ 
        width: "100%", 
        padding: "40px 20px", 
        background: "#F5F7FA",
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // Allow access if user has organization subscription OR user-level trial
  // Priority: if backend says user has access, always allow access
  if (hasActiveSubscription || (accessStatus?.has_access) || subscriptionData?.is_free_trial) {
    console.log("[SubscriptionGate] Access granted:", {
      hasActiveSubscription,
      backendHasAccess: accessStatus?.has_access,
      isFreeTrial: subscriptionData?.is_free_trial,
      accessReason: accessStatus?.reason
    });
    return <>{children}</>;
  }

  console.log("[SubscriptionGate] Access denied:", {
    hasActiveSubscription,
    backendHasAccess: accessStatus?.has_access,
    isFreeTrial: subscriptionData?.is_free_trial,
    needsOrganization: accessStatus?.needs_organization,
    trialDaysRemaining: accessStatus?.trial_days_remaining,
    accessReason: accessStatus?.reason
  });

  // Show different content for users with user-level trials vs no access
  // Only show org creation if user doesn't have access but has trial info
  if (accessStatus?.needs_organization && accessStatus?.trial_days_remaining !== undefined && !accessStatus?.has_access) {
    // User has user-level trial but needs organization
    return (
      <div style={{ 
        width: "100%", 
        padding: "40px 20px", 
        background: "#F5F7FA",
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <Card 
          style={{ 
            maxWidth: 600, 
            width: "100%",
            textAlign: "center",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)"
          }}
        >
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <div>
              <CrownOutlined 
                style={{ 
                  fontSize: "48px", 
                  color: "#1890ff",
                  marginBottom: "16px"
                }} 
              />
              <Title level={2} style={{ marginBottom: "8px" }}>
                {accessStatus.trial_days_remaining > 0 
                  ? `${accessStatus.trial_days_remaining} Days Left in Trial`
                  : "Trial Expired"
                }
              </Title>
              <Text type="secondary" style={{ fontSize: "16px" }}>
                {accessStatus.trial_days_remaining > 0 
                  ? `You have full access to ${feature} during your trial. Create or join an organization to continue after your trial ends.`
                  : `Your trial has expired. Create or join an organization to continue using ${feature}.`
                }
              </Text>
            </div>

            <Alert
              message="Getting Started"
              description={
                <div style={{ textAlign: "left" }}>
                  <Paragraph style={{ marginBottom: "12px" }}>
                    To continue using CodePilot after your trial:
                  </Paragraph>
                  <ul style={{ paddingLeft: "20px", margin: 0 }}>
                    <li><strong>Create an organization</strong> - Get a 14-day organizational trial, then subscribe</li>
                    <li><strong>Join an existing organization</strong> - Ask for an email invite from your team</li>
                  </ul>
                </div>
              }
              type="info"
              showIcon
              icon={<CheckCircleOutlined />}
              style={{ textAlign: "left" }}
            />

            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="large"
                onClick={() => setShowCreateModal(true)}
                style={{ width: "100%" }}
              >
                Create New Organization
              </Button>
            </Space>
          </Space>

          <CreateOrganizationModal
            open={showCreateModal}
            onCancel={() => setShowCreateModal(false)}
            onSuccess={handleCreateSuccess}
          />
        </Card>
      </div>
    );
  }

  // Default blocked access (no trial, no subscription)
  return (
    <div style={{ 
      width: "100%", 
      padding: "40px 20px", 
      background: "#F5F7FA",
      minHeight: "60vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }}>
      <Card 
        style={{ 
          maxWidth: 600, 
          width: "100%",
          textAlign: "center",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)"
        }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <LockOutlined 
              style={{ 
                fontSize: "48px", 
                color: "#1890ff",
                marginBottom: "16px"
              }} 
            />
            <Title level={2} style={{ marginBottom: "8px" }}>
              Active Subscription Required
            </Title>
            <Text type="secondary" style={{ fontSize: "16px" }}>
              Access to {feature} requires an active subscription
            </Text>
          </div>

          <Alert
            message="Subscription Benefits"
            description={
              <div style={{ textAlign: "left" }}>
                <Paragraph style={{ marginBottom: "12px" }}>
                  With an active subscription, you'll have access to:
                </Paragraph>
                <ul style={{ paddingLeft: "20px", margin: 0 }}>
                  <li>Advanced Code Identification tools</li>
                  <li>Unlimited file processing</li>
                  <li>Priority support</li>
                  <li>Latest AI models and features</li>
                </ul>
              </div>
            }
            type="info"
            showIcon
            icon={<CheckCircleOutlined />}
            style={{ textAlign: "left" }}
          />

          <div>
            <Paragraph style={{ marginBottom: "16px" }}>
              Please contact your organization administrator to activate or renew your subscription.
            </Paragraph>
            <Button 
              type="primary" 
              size="large"
              icon={<CrownOutlined />}
              onClick={() => {
                window.location.href = "/profile";
              }}
            >
              View Organization Details
            </Button>
          </div>
          
          <Divider />
          
          <Alert
            message="Need Help?"
            description={
              <div style={{ textAlign: "center" }}>
                <CustomerServiceOutlined style={{ marginRight: "8px" }} />
                Contact our support team at{" "}
                <a href="mailto:help@flycodepilot.com" style={{ color: "#1890ff" }}>
                  help@flycodepilot.com
                </a>
              </div>
            }
            type="info"
            showIcon={false}
            style={{ textAlign: "center" }}
          />
        </Space>
      </Card>
    </div>
  );
}; 